import { useEffect, useState } from 'react';

import type { ReactNode } from 'react';

import styled from 'styled-components';

import { Box, IconButton, Typography } from '@mui/material';

import { Icon } from '@sdb/planning-ui';

const ArrowNavigationStyles = styled.div`
  display: inline-flex;
  align-self: flex-end;
  align-items: center;
  justify-content: center;

  button {
    margin: 0 5px;
  }

  .cnt-box {
    min-width: 36px;
    text-align: center;
  }
`;

type Props<T> = {
  onNavigate: (direction: 'prev' | 'next', index: number) => void;
  options: T[];
  selectedOption: T;
  disabled?: boolean;
  showCnt?: boolean;
  shouldHideArrows?: boolean;
  children?: ReactNode | ReactNode[];
  isToolbar?: boolean;
  /** accessible names for the icon-only arrows; without them the buttons have no name */
  prevLabel?: string;
  nextLabel?: string;
};

export default function ArrowNavigation<T>({
  onNavigate,
  options,
  selectedOption,
  disabled = false,
  showCnt,
  children,
  shouldHideArrows = false,
  isToolbar = false,
  prevLabel,
  nextLabel
}: Props<T>) {
  const [isPrevDisabled, setPrevDisabled] = useState(true);
  const [isNextDisabled, setNextDisabled] = useState(false);

  useEffect(() => {
    const index = options.indexOf(selectedOption);
    setPrevDisabled(index === 0 || index === -1);
    setNextDisabled(index === options.length - 1);
  }, [options, selectedOption]);

  return (
    <ArrowNavigationStyles className="arrow-navigation">
      {!shouldHideArrows && (
        <IconButton
          onClick={() =>
            onNavigate(
              'prev',
              selectedOption ? options.indexOf(selectedOption) : 1
            )
          }
          color={isToolbar ? 'inherit' : 'primary'}
          disabled={disabled || isPrevDisabled}
          size="small"
          aria-label={prevLabel}
        >
          <Icon type="chevron-left" fontSize="small" />
        </IconButton>
      )}
      {showCnt && (
        <Typography
          className="d-flex v-center h-center cnt-box"
          variant="subtitle2"
          component="span"
        >
          {options.length && (
            <>
              {options.indexOf(selectedOption) + 1} /{options.length}
            </>
          )}
        </Typography>
      )}
      {children && <Box px={2}>{children}</Box>}
      {!shouldHideArrows && (
        <IconButton
          onClick={() =>
            onNavigate(
              'next',
              selectedOption ? options.indexOf(selectedOption) : 1
            )
          }
          color={isToolbar ? 'inherit' : 'primary'}
          disabled={disabled || isNextDisabled}
          size="small"
          aria-label={nextLabel}
        >
          <Icon type="chevron-right" fontSize="small" />
        </IconButton>
      )}
    </ArrowNavigationStyles>
  );
}
